/**
 * K2 Eligibility Modal
 *
 * Modal that appears after SIE file upload for fiscal years starting 2026-01-01 or later.
 * User must confirm K2 eligibility before proceeding to Step 2.
 */

import { useState, useRef } from "react";
import {
  ShieldCheck,
  ShieldAlert,
  XCircle,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { reportsApi, type AnnualReport, type UserInput } from "@/lib/api";

interface K2EligibilityModalProps {
  isOpen: boolean;
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  onConfirmed: (passes: boolean) => void;
  onClose?: () => void;
  /** Custom save handler for guest mode (bypasses API) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
}

export function K2EligibilityModal({
  isOpen,
  report,
  onUpdate,
  onConfirmed,
  onClose,
  onSaveInput,
}: K2EligibilityModalProps) {
  const userInput = report.userInput as UserInput;

  const [k2Eligibility, setK2Eligibility] = useState<{
    hasForeignBranches: boolean | null;
    hasCryptoAssets: boolean | null;
    hasShareBasedPayments: boolean | null;
  }>(() => ({
    hasForeignBranches: userInput?.k2Eligibility?.hasForeignBranches ?? null,
    hasCryptoAssets: userInput?.k2Eligibility?.hasCryptoAssets ?? null,
    hasShareBasedPayments: userInput?.k2Eligibility?.hasShareBasedPayments ?? null,
  }));
  const [isSaving, setIsSaving] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const allAnswered =
    k2Eligibility.hasForeignBranches !== null &&
    k2Eligibility.hasCryptoAssets !== null &&
    k2Eligibility.hasShareBasedPayments !== null;

  const passes =
    !k2Eligibility.hasForeignBranches &&
    !k2Eligibility.hasCryptoAssets &&
    !k2Eligibility.hasShareBasedPayments;

  const handleConfirm = async () => {
    if (!allAnswered) return;
    setIsSaving(true);

    try {
      const saveData = {
        ...userInput,
        k2Eligibility: {
          hasForeignBranches: k2Eligibility.hasForeignBranches ?? false,
          hasCryptoAssets: k2Eligibility.hasCryptoAssets ?? false,
          hasShareBasedPayments: k2Eligibility.hasShareBasedPayments ?? false,
          confirmedAt: new Date().toISOString(),
        },
      };

      // Use custom save handler for guest mode, otherwise use API
      if (onSaveInput) {
        const result = await onSaveInput(saveData);
        if (result.success && result.report) {
          onUpdate(result.report);
          toast.success("K2-behörighet bekräftad");
          onConfirmed(passes);
        } else {
          toast.error(result.error || "Kunde inte spara K2-behörighet");
        }
      } else {
        const response = await reportsApi.updateInput(report.id, saveData);
        if (response.success && response.data) {
          onUpdate(response.data.report);
          toast.success("K2-behörighet bekräftad");
          onConfirmed(passes);
        } else {
          toast.error("Kunde inte spara K2-behörighet");
        }
      }
    } catch (err) {
      console.error("Failed to save K2 eligibility:", err);
      toast.error("Kunde inte spara K2-behörighet");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      onClose &&
      e.target === backdropRef.current &&
      mouseDownTargetRef.current === backdropRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="fixed inset-0 bg-black/50 transition-opacity"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <ShieldAlert size={24} className="text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                K2-behörighetskontroll för 2026
              </h2>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Från och med räkenskapsår som börjar 2026-01-01 eller senare gäller nya regler
              för vilka företag som får tillämpa K2. Besvara frågorna nedan.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Referens: BFNAR 2016:10 punkt 1.1A
            </p>

            <div className="space-y-4">
              {/* Question 1: Foreign branches */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-800 mb-3">
                  1. Har företaget filialer i utlandet?
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-foreign-branches"
                      checked={k2Eligibility.hasForeignBranches === true}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasForeignBranches: true }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Ja</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-foreign-branches"
                      checked={k2Eligibility.hasForeignBranches === false}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasForeignBranches: false }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Nej</span>
                  </label>
                </div>
              </div>

              {/* Question 2: Crypto assets */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-800 mb-1">
                  2. Har företaget kryptotillgångar?
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Undantag: Enstaka transaktioner där kryptovaluta använts som betalningsmedel räknas inte.
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-crypto-assets"
                      checked={k2Eligibility.hasCryptoAssets === true}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasCryptoAssets: true }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Ja</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-crypto-assets"
                      checked={k2Eligibility.hasCryptoAssets === false}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasCryptoAssets: false }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Nej</span>
                  </label>
                </div>
              </div>

              {/* Question 3: Share-based payments */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-800 mb-1">
                  3. Har företaget aktierelaterade ersättningar?
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  T.ex. personaloptioner, aktiesparprogram eller liknande incitamentsprogram.
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-share-based"
                      checked={k2Eligibility.hasShareBasedPayments === true}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasShareBasedPayments: true }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Ja</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="k2-modal-share-based"
                      checked={k2Eligibility.hasShareBasedPayments === false}
                      onChange={() => setK2Eligibility(prev => ({ ...prev, hasShareBasedPayments: false }))}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Nej</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Result message */}
            {allAnswered && (
              <div className={`mt-4 p-3 rounded-lg ${passes ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {passes ? (
                    <>
                      <ShieldCheck size={18} className="text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Företaget uppfyller kraven för K2
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle size={18} className="text-red-600" />
                      <span className="text-sm font-medium text-red-800">
                        Företaget uppfyller INTE kraven för K2
                      </span>
                    </>
                  )}
                </div>
                {!passes && (
                  <p className="text-xs text-red-600 mt-1 ml-6">
                    Företag med filialer i utlandet, kryptotillgångar eller aktierelaterade ersättningar
                    får inte tillämpa K2 för räkenskapsår som börjar 2026 eller senare.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            <button
              onClick={handleConfirm}
              disabled={!allAnswered || isSaving}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sparar...
                </>
              ) : (
                <>
                  <ShieldCheck size={16} />
                  Bekräfta och fortsätt
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
